if (!customElements.get('audio-player-bar')) {
  const PEAK_COUNT = 800;
  const BAR_WIDTH = 2;
  const BAR_GAP = 1;
  const SEEK_STEP_SECONDS = 5;

  // One decoded waveform per MP3 url, shared for the lifetime of the page.
  const waveformCache = new Map();

  const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = Math.floor(safeSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainder}`;
  };

  async function decodePeaks(src) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) throw new Error('Web Audio API not supported');

    const response = await fetch(src);
    if (!response.ok) throw new Error(`Audio request failed: ${response.status}`);
    const data = await response.arrayBuffer();

    const context = new AudioContextClass();
    try {
      // Callback form keeps older Safari working.
      const buffer = await new Promise((resolve, reject) => context.decodeAudioData(data, resolve, reject));
      const channels = Array.from({ length: buffer.numberOfChannels }, (_, index) => buffer.getChannelData(index));
      const blockSize = Math.max(1, Math.floor(buffer.length / PEAK_COUNT));
      const stride = Math.max(1, Math.floor(blockSize / 256));
      const peaks = new Float32Array(PEAK_COUNT);
      let highest = 0;

      for (let peakIndex = 0; peakIndex < PEAK_COUNT; peakIndex++) {
        const start = peakIndex * blockSize;
        const end = Math.min(start + blockSize, buffer.length);
        let peak = 0;
        for (let sample = start; sample < end; sample += stride) {
          for (const channel of channels) {
            const value = Math.abs(channel[sample]);
            if (value > peak) peak = value;
          }
        }
        peaks[peakIndex] = peak;
        if (peak > highest) highest = peak;
      }

      if (highest > 0) {
        for (let peakIndex = 0; peakIndex < PEAK_COUNT; peakIndex++) peaks[peakIndex] /= highest;
      }
      return peaks;
    } finally {
      if (context.close) context.close();
    }
  }

  // Deterministic stand-in when the file can't be decoded (e.g. CORS), so a track always looks the same.
  function generatedPeaks(src) {
    let seed = 0;
    for (let index = 0; index < src.length; index++) seed = (Math.imul(31, seed) + src.charCodeAt(index)) | 0;
    const random = () => {
      seed = (seed + 0x6d2b79f5) | 0;
      let value = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };

    const peaks = new Float32Array(PEAK_COUNT);
    let level = 0.6;
    for (let index = 0; index < PEAK_COUNT; index++) {
      if (index % 40 === 0) level = 0.25 + random() * 0.75;
      peaks[index] = Math.min(1, level * (0.75 + random() * 0.25));
    }
    return peaks;
  }

  function loadPeaks(src) {
    if (!waveformCache.has(src)) {
      waveformCache.set(
        src,
        decodePeaks(src).catch(() => generatedPeaks(src))
      );
    }
    return waveformCache.get(src);
  }

  class AudioPlayerBar extends HTMLElement {
    connectedCallback() {
      this.audio = this.querySelector('.audio-player-bar__audio');
      this.toggleButton = this.querySelector('.audio-player-bar__toggle');
      this.closeButton = this.querySelector('.audio-player-bar__close');
      this.waveform = this.querySelector('.audio-player-bar__waveform');
      this.canvas = this.querySelector('.audio-player-bar__canvas');
      this.artLink = this.querySelector('.audio-player-bar__art');
      this.image = this.querySelector('.audio-player-bar__image');
      this.titleElement = this.querySelector('.audio-player-bar__title');
      this.genre = this.querySelector('.audio-player-bar__genre');
      this.genreText = this.querySelector('.audio-player-bar__genre-text');
      this.status = this.querySelector('.audio-player-bar__status');

      this.track = null;
      this.peaks = null;
      this.progress = 0;
      this.state = 'closed';
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggleButton.addEventListener('click', () => this.togglePlayback(), { signal });
      this.closeButton.addEventListener('click', () => this.close(), { signal });

      this.audio.addEventListener('playing', () => this.setState('playing'), { signal });
      this.audio.addEventListener(
        'waiting',
        () => {
          if (!this.audio.paused) this.setState('loading');
        },
        { signal }
      );
      this.audio.addEventListener(
        'pause',
        () => {
          // A pause queued by switching tracks must not override the new track's state.
          if (this.audio.paused && this.state !== 'error') this.setState('paused');
        },
        { signal }
      );
      this.audio.addEventListener(
        'ended',
        () => {
          this.audio.currentTime = 0;
          this.updateProgress();
          this.setState('paused');
        },
        { signal }
      );
      this.audio.addEventListener('error', () => this.setState('error'), { signal });
      this.audio.addEventListener('timeupdate', () => this.updateProgress(), { signal });
      this.audio.addEventListener('loadedmetadata', () => this.updateProgress(), { signal });

      this.waveform.addEventListener('pointerdown', this.onPointerDown.bind(this), { signal });
      this.waveform.addEventListener('pointermove', this.onPointerMove.bind(this), { signal });
      this.waveform.addEventListener('pointerup', this.onPointerUp.bind(this), { signal });
      this.waveform.addEventListener('pointercancel', this.onPointerUp.bind(this), { signal });
      this.waveform.addEventListener('keydown', this.onWaveformKeyDown.bind(this), { signal });

      this.resizeObserver = new ResizeObserver(() => this.draw());
      this.resizeObserver.observe(this.waveform);
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.resizeObserver.disconnect();
      this.stopAnimation();
      this.audio.pause();
    }

    get currentSrc() {
      return this.track ? this.track.src : null;
    }

    // Called by card buttons: plays a new track, or toggles the current one.
    toggle(track) {
      this.opener = document.activeElement;
      if (this.track && this.track.src === track.src) {
        this.open();
        this.togglePlayback();
        return;
      }
      this.load(track);
      this.play();
    }

    togglePlayback() {
      if (!this.track || this.state === 'error') return;
      if (this.audio.paused) {
        this.play();
      } else {
        this.audio.pause();
      }
    }

    load(track) {
      this.track = track;
      this.state = 'idle';
      this.progress = 0;
      this.peaks = null;
      this.audio.src = track.src;

      this.artLink.href = track.url || '#';
      this.artLink.setAttribute('aria-label', track.title || '');
      if (track.image) {
        this.image.src = track.image;
        this.image.alt = track.title || '';
        this.image.hidden = false;
      } else {
        this.image.removeAttribute('src');
        this.image.hidden = true;
      }

      this.titleElement.textContent = track.title || '';
      this.titleElement.title = track.title || '';
      this.titleElement.hidden = !track.title;

      this.genreText.textContent = track.genre || '';
      this.genre.hidden = !track.genre;
      this.status.textContent = this.dataset.labelNowPlaying.replace('[title]', track.title || '');

      this.open();
      this.updateProgress();

      loadPeaks(track.src).then((peaks) => {
        if (this.currentSrc !== track.src) return;
        this.peaks = peaks;
        this.draw();
      });
    }

    async play() {
      this.setState('loading');
      try {
        await this.audio.play();
      } catch (error) {
        // AbortError means the source changed or pause() ran before playback started.
        if (error.name !== 'AbortError') this.setState('error');
      }
    }

    open() {
      if (!this.hidden && this.classList.contains('is-open')) return;
      clearTimeout(this.hideTimer);
      this.hidden = false;
      document.body.classList.add('audio-player-open');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.draw();
      });
    }

    close() {
      this.audio.pause();
      if (this.state !== 'error') this.setState('paused');
      this.classList.remove('is-open');
      document.body.classList.remove('audio-player-open');
      this.hideTimer = setTimeout(() => {
        this.hidden = true;
      }, 300);

      if (this.opener && this.opener.isConnected && this.contains(document.activeElement)) {
        this.opener.focus();
      }
    }

    setState(state) {
      this.state = state;
      const isPlaying = state === 'playing';
      const isLoading = state === 'loading';

      this.classList.toggle('is-playing', isPlaying);
      this.classList.toggle('is-loading', isLoading);
      this.classList.toggle('is-unavailable', state === 'error');

      this.querySelector('.audio-player-bar__icon--play').classList.toggle('hidden', isPlaying || isLoading);
      this.querySelector('.audio-player-bar__icon--pause').classList.toggle('hidden', !isPlaying);
      this.querySelector('.audio-player-bar__spinner').classList.toggle('hidden', !isLoading);

      let label = this.dataset.labelPlay;
      if (isPlaying || isLoading) label = this.dataset.labelPause;
      if (state === 'error') {
        label = this.dataset.labelUnavailable;
        this.status.textContent = label;
      }
      this.toggleButton.setAttribute('aria-label', label);
      this.toggleButton.disabled = state === 'error';

      if (isPlaying) {
        this.startAnimation();
      } else {
        this.stopAnimation();
      }

      document.dispatchEvent(
        new CustomEvent('audio-player:change', { detail: { src: this.currentSrc, state } })
      );
    }

    startAnimation() {
      if (this.animationFrame) return;
      const tick = () => {
        this.updateProgress();
        this.animationFrame = requestAnimationFrame(tick);
      };
      this.animationFrame = requestAnimationFrame(tick);
    }

    stopAnimation() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    updateProgress() {
      const { currentTime, duration } = this.audio;
      this.progress = Number.isFinite(duration) && duration > 0 ? currentTime / duration : 0;
      this.waveform.setAttribute('aria-valuenow', Math.round(this.progress * 100));
      this.waveform.setAttribute('aria-valuetext', `${formatTime(currentTime)} / ${formatTime(duration)}`);
      this.draw();
    }

    seekToRatio(ratio) {
      const { duration } = this.audio;
      if (!Number.isFinite(duration) || duration <= 0) return;
      this.audio.currentTime = Math.min(Math.max(ratio, 0), 1) * duration;
      this.updateProgress();
    }

    seekFromPointer(event) {
      const rect = this.waveform.getBoundingClientRect();
      this.seekToRatio((event.clientX - rect.left) / rect.width);
    }

    onPointerDown(event) {
      if (!this.track || event.button !== 0) return;
      this.isScrubbing = true;
      this.waveform.setPointerCapture(event.pointerId);
      this.seekFromPointer(event);
    }

    onPointerMove(event) {
      if (this.isScrubbing) this.seekFromPointer(event);
    }

    onPointerUp(event) {
      if (!this.isScrubbing) return;
      this.isScrubbing = false;
      if (this.waveform.hasPointerCapture(event.pointerId)) this.waveform.releasePointerCapture(event.pointerId);
    }

    onWaveformKeyDown(event) {
      const { currentTime, duration } = this.audio;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const targets = {
        ArrowRight: currentTime + SEEK_STEP_SECONDS,
        ArrowUp: currentTime + SEEK_STEP_SECONDS,
        ArrowLeft: currentTime - SEEK_STEP_SECONDS,
        ArrowDown: currentTime - SEEK_STEP_SECONDS,
        Home: 0,
        End: duration,
      };
      if (!(event.key in targets)) return;

      event.preventDefault();
      this.seekToRatio(targets[event.key] / duration);
    }

    draw() {
      const width = this.waveform.clientWidth;
      const height = this.waveform.clientHeight;
      if (!width || !height) return;

      const ratio = window.devicePixelRatio || 1;
      const canvasWidth = Math.round(width * ratio);
      const canvasHeight = Math.round(height * ratio);
      if (this.canvas.width !== canvasWidth || this.canvas.height !== canvasHeight) {
        this.canvas.width = canvasWidth;
        this.canvas.height = canvasHeight;
        this.colors = null;
      }

      if (!this.colors) {
        const styles = getComputedStyle(this);
        this.colors = {
          wave: styles.getPropertyValue('--audio-player-wave').trim() || 'rgba(255, 255, 255, 0.45)',
          played: styles.getPropertyValue('--audio-player-wave-played').trim() || '#ffffff',
          playhead: styles.getPropertyValue('--audio-player-playhead').trim() || '#ffffff',
          playedStops: styles
            .getPropertyValue('--audio-player-wave-played-stops')
            .split(',')
            .map((color) => color.trim())
            .filter(Boolean),
        };
      }

      const context = this.canvas.getContext('2d');
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      // Optional gradient for the played bars (spans the full waveform width).
      let played = this.colors.played;
      if (this.colors.playedStops.length > 1) {
        played = context.createLinearGradient(0, 0, width, 0);
        const stops = this.colors.playedStops;
        stops.forEach((color, index) => played.addColorStop(index / (stops.length - 1), color));
      }

      const step = BAR_WIDTH + BAR_GAP;
      const barCount = Math.floor(width / step);
      const progressX = this.progress * width;

      for (let bar = 0; bar < barCount; bar++) {
        let peak = 0;
        if (this.peaks) {
          const from = Math.floor((bar / barCount) * this.peaks.length);
          const to = Math.max(from + 1, Math.floor(((bar + 1) / barCount) * this.peaks.length));
          for (let index = from; index < to; index++) peak = Math.max(peak, this.peaks[index]);
        }

        const barHeight = Math.max(2, peak * height);
        const x = bar * step;
        context.fillStyle = x + BAR_WIDTH <= progressX ? played : this.colors.wave;
        context.fillRect(x, (height - barHeight) / 2, BAR_WIDTH, barHeight);
      }

      if (this.track) {
        context.fillStyle = this.colors.playhead;
        context.fillRect(Math.min(progressX, width - 2), 0, 2, height);
      }
    }
  }

  customElements.define('audio-player-bar', AudioPlayerBar);
}
